<template>
  <div>
    <v-layout row wrap align-center class="mt-3 mx-4 mb-1">
      <v-icon color="success" size="30" class="mr-3">mdi-cash</v-icon>
      <div>
        <div class="headline">Deductions</div>
        <div class="caption grey--text">
          Recorded deductions for the season. Open one to see — and edit —
          the details behind it.
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn outline small color="success" class="mr-2" @click="openExportMissing">
        <v-icon small class="mr-1">mdi-file-excel</v-icon>
        Export missing
      </v-btn>
      <v-btn outline small color="success" class="mr-2" @click="pickImport">
        <v-icon small class="mr-1">mdi-upload</v-icon>
        Import
      </v-btn>
      <input
        ref="importFile"
        type="file"
        accept=".xlsx,.xls"
        style="display: none"
        @change="onImportFileSelected"
      />
      <v-btn outline small color="success" :loading="loading" @click="refresh">
        <v-icon small class="mr-1">mdi-refresh</v-icon>
        Refresh
      </v-btn>
    </v-layout>

    <v-layout row wrap align-center class="mx-4 mb-3">
      <div class="mode-toggle-group">
        <button
          type="button"
          class="mode-toggle-btn"
          :class="{ 'mode-toggle-btn--active': mode === 'list' }"
          @click="mode = 'list'"
        >
          <v-icon small class="mr-1">mdi-format-list-bulleted</v-icon>
          List
        </button>
        <button
          type="button"
          class="mode-toggle-btn"
          :class="{ 'mode-toggle-btn--active': mode === 'calendar' }"
          @click="mode = 'calendar'"
        >
          <v-icon small class="mr-1">mdi-calendar</v-icon>
          Calendar
        </button>
      </div>

      <template v-if="mode === 'calendar'">
        <v-spacer></v-spacer>
        <v-btn outline small color="success" class="cal-nav-btn" @click="shiftMonth(-1)">
          <v-icon small>mdi-chevron-left</v-icon>
        </v-btn>
        <div class="title mx-3 success--text">{{ monthLabel }}</div>
        <v-btn outline small color="success" class="cal-nav-btn" @click="shiftMonth(1)">
          <v-icon small>mdi-chevron-right</v-icon>
        </v-btn>
      </template>
    </v-layout>

    <dx-grid
      v-if="mode === 'list'"
      ref="grid"
      url="deductions"
      :columns="listColumns"
      :actions="listActions"
      :hideHeader="true"
      :isRemotePaging="false"
      :onLoaded="onListLoaded"
      :onRowClicked="openRow"
      navigationPath="openRow"
      navigationField="id"
    ></dx-grid>

    <div v-else class="mx-4 mb-4">
      <v-sheet class="cal-sheet">
        <div class="cal-grid">
          <div v-for="name in weekNames" :key="name" class="cal-head">{{ name }}</div>
          <div
            v-for="day in calendarDays"
            :key="day.key"
            class="cal-day"
            :class="{ 'cal-day--muted': !day.inMonth, 'cal-day--today': isToday(day.key) }"
          >
            <div class="cal-day-number" :class="{ 'cal-day-number--today': isToday(day.key) }">
              {{ day.label }}
            </div>
            <div v-if="!day.rows.length && day.inMonth" class="cal-day-empty"></div>
            <div
              v-for="row in day.rows"
              :key="row.id"
              class="cal-chip"
              @click="openRow(row)"
            >
              <span class="cal-chip-bus">#{{ row.busNumber }}</span>
              <span class="cal-chip-amount">${{ money(row.amount) }}</span>
            </div>
          </div>
        </div>
      </v-sheet>
    </div>

    <deduction-detail-dialog
      v-model="detailDialog"
      :row="detailRow"
      @saved="reloadAll"
    ></deduction-detail-dialog>

    <v-dialog v-model="exportDialog" max-width="480">
      <v-card>
        <v-card-title class="success white--text py-2 px-4">
          <span class="title pt-1">Export missing</span>
          <v-spacer></v-spacer>
          <v-btn class="white--text my-0" icon flat small dark @click="exportDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="caption grey--text mb-3">
            The season fills the dates. You can change the from and to dates before exporting.
          </div>
          <v-select
            v-model="exportSeason"
            :items="exportSeasons"
            item-text="season"
            item-value="season"
            label="Season"
            color="success"
            @change="onExportSeason"
          ></v-select>
          <v-layout row>
            <v-flex xs6 class="pr-2">
              <v-text-field
                v-model="exportFrom"
                label="From"
                type="date"
                color="success"
              ></v-text-field>
            </v-flex>
            <v-flex xs6 class="pl-2">
              <v-text-field
                v-model="exportTo"
                label="To"
                type="date"
                color="success"
              ></v-text-field>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions class="px-2 py-3">
          <v-spacer></v-spacer>
          <v-btn dark color="default" @click="exportDialog = false">Cancel</v-btn>
          <v-btn
            color="success"
            :loading="exporting"
            :disabled="!exportFrom || !exportTo"
            @click="exportMissing"
          >
            Export
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="importPreviewDialog" max-width="800" scrollable persistent>
      <v-card>
        <v-card-title class="success white--text py-2 px-4">
          <span class="title pt-1">Review Import</span>
          <v-spacer></v-spacer>
          <v-btn class="white--text my-0" icon flat small dark @click="cancelImport">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4" style="max-height: 65vh">
          <div v-if="importPreviewLoading" class="text-xs-center py-4">
            <v-progress-circular indeterminate color="success"></v-progress-circular>
          </div>
          <template v-else>
            <div class="caption grey--text mb-2">
              {{ importPreviewRows.length }} row(s) will be imported
              <span v-if="importPreviewSkipped">, {{ importPreviewSkipped }} left blank</span>.
              This will overwrite any existing deduction for the same driver/date/kind.
            </div>

            <v-alert v-if="importPreviewErrors.length" :value="true" type="error" class="mb-3">
              <div v-for="(err, i) in importPreviewErrors" :key="i">{{ err }}</div>
            </v-alert>

            <v-data-table
              :headers="importPreviewHeaders"
              :items="importPreviewRows"
              hide-actions
              class="import-preview-table"
            >
              <template v-slot:items="props">
                <tr>
                  <td>{{ formatDate(props.item.date) }}</td>
                  <td>{{ props.item.busNumber || props.item.driverBusID }}</td>
                  <td>{{ props.item.driver }}</td>
                  <td>{{ props.item.kind }}</td>
                  <td>{{ props.item.preset }}</td>
                  <td>${{ money(props.item.amount) }}</td>
                </tr>
              </template>
            </v-data-table>
          </template>
        </v-card-text>
        <v-card-actions class="px-2 py-3">
          <v-spacer></v-spacer>
          <v-btn dark color="default" @click="cancelImport">Cancel</v-btn>
          <v-btn
            color="success"
            :disabled="importPreviewLoading || !importPreviewRows.length"
            :loading="importCommitting"
            @click="confirmImport"
          >
            Import {{ importPreviewRows.length }} row(s)
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import deductionDetailDialog from "./components/DeductionDetailDialog.vue";

export default {
  components: {
    deductionDetailDialog,
  },
  data() {
    return {
      loading: false,
      listTotal: 0,
      mode: "list",
      monthStart: moment().startOf("month").format("YYYY-MM-DD"),
      rows: [],
      weekNames: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      listActions: [
        {
          align: "left",
          type: "text",
          getText: () =>
            '<span style="font-size:15px;font-weight:600;color:#2e7d32;padding-left:4px;">' +
            this.listTotal +
            " records</span>",
        },
      ],

      listColumns: [
        {
          label: "Date",
          field: "date",
          type: "date",
          format: "MM/dd/yyyy",
          sortOrder: "desc",
          sortIndex: 0,
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Bus",
          field: "busNumber",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Driver",
          field: "driver",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Category",
          field: "categoryLabel",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Preset",
          field: "preset",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Amount",
          field: "amount",
          type: "number",
          format: { type: "currency", precision: 2 },
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Entered By",
          field: "enteredBy",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Entered On",
          field: "enteredOn",
          type: "date",
          format: "MM/dd/yyyy hh:mm:ss a",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
      ],

      detailDialog: false,
      detailRow: null,

      // "Review before import" — parses the spreadsheet server-side without saving, then lets
      // the user confirm before anything is actually written.
      importPreviewDialog: false,
      importPreviewLoading: false,
      importPreviewRows: [],
      importPreviewErrors: [],
      importPreviewSkipped: 0,
      importPreviewFile: null,
      importCommitting: false,
      exportDialog: false,
      exporting: false,
      exportSeason: "",
      exportSeasons: [],
      exportFrom: "",
      exportTo: "",
      importPreviewHeaders: [
        { text: "Date", value: "date" },
        { text: "Bus", value: "busNumber" },
        { text: "Driver", value: "driver" },
        { text: "Kind", value: "kind" },
        { text: "Preset", value: "preset" },
        { text: "Amount", value: "amount" },
      ],
    };
  },
  computed: {
    monthLabel() {
      return moment(this.monthStart).format("MMMM YYYY");
    },
    calendarDays() {
      const start = moment(this.monthStart).startOf("week");
      const end = moment(this.monthStart).endOf("month").endOf("week");
      const days = [];
      const cursor = start.clone();
      while (cursor.isSameOrBefore(end, "day")) {
        const key = cursor.format("YYYY-MM-DD");
        days.push({
          key: key,
          label: cursor.format("D"),
          inMonth: cursor.month() === moment(this.monthStart).month(),
          rows: this.rows.filter(
            (row) => moment(row.date).format("YYYY-MM-DD") === key
          ),
        });
        cursor.add(1, "day");
      }
      return days;
    },
  },
  async created() {
    await this.loadCalendarRows();
  },
  methods: {
    money(value) {
      return Number(value || 0).toFixed(2);
    },
    onListLoaded(result) {
      const rows = Array.isArray(result)
        ? result
        : result && Array.isArray(result.data)
        ? result.data
        : [];
      this.listTotal = rows.length;
    },
    formatDate(value) {
      return value ? moment(value).format("M/D/YYYY") : "";
    },
    // Refresh button — reloads whichever view is currently active.
    async refresh() {
      if (this.mode === "list") {
        if (this.$refs.grid) this.$refs.grid.reloadGrid();
        return;
      }
      await this.loadCalendarRows();
    },
    // After a save or import, both views may now be stale — refresh whichever is visible and
    // silently refresh the other's cached data too so switching modes doesn't show old data.
    async reloadAll() {
      if (this.$refs.grid) this.$refs.grid.reloadGrid();
      await this.loadCalendarRows();
    },
    async loadCalendarRows() {
      const fromDate = moment(this.monthStart).startOf("week").format("YYYY-MM-DD");
      const toDate = moment(this.monthStart)
        .endOf("month")
        .endOf("week")
        .format("YYYY-MM-DD");
      this.loading = true;
      try {
        this.rows = await this.$http.get(
          "api/driverAbsent/getDeductions?fromDate=" + fromDate + "&toDate=" + toDate
        );
      } catch (e) {
        this.rows = [];
        this.$notify({
          group: "notification",
          type: "error",
          text: (e && e.body) || "Failed to load deductions",
        });
      } finally {
        this.loading = false;
      }
    },
    async shiftMonth(delta) {
      this.monthStart = moment(this.monthStart)
        .add(delta, "month")
        .startOf("month")
        .format("YYYY-MM-DD");
      await this.loadCalendarRows();
    },
    isToday(dateKey) {
      return dateKey === moment().format("YYYY-MM-DD");
    },
    openRow(row) {
      this.detailRow = row;
      this.detailDialog = true;
    },
    downloadBlob(data, filename) {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    // Uses the app's authenticated axios instance ($http) — a plain axios call skips the
    // auth-token request interceptor and the export comes back 401.
    async openExportMissing() {
      this.exportDialog = true;
      try {
        if (!this.exportSeasons.length) {
          this.exportSeasons =
            (await this.$http.get("api/driverAbsent/getDeductionSeasons")) || [];
        }
        const reportSeason = localStorage.getItem("deductionReportSeason");
        const saved = this.exportSeasons.find((s) => s.season === reportSeason);
        const today = moment();
        const current = this.exportSeasons.find(
          (s) =>
            s.fromDate &&
            moment(s.fromDate).isSameOrBefore(today, "day") &&
            (!s.toDate || moment(s.toDate).isSameOrAfter(today, "day"))
        );
        this.onExportSeason((saved || current || this.exportSeasons[0] || {}).season);
      } catch (e) {
        this.$notify({
          group: "notification",
          type: "error",
          text: (e && e.body) || "Failed to load seasons",
        });
      }
    },
    onExportSeason(name) {
      const picked = this.exportSeasons.find((s) => s.season === name);
      if (!picked) return;
      this.exportSeason = picked.season;
      this.exportFrom = picked.fromDate
        ? moment(picked.fromDate).format("YYYY-MM-DD")
        : this.exportFrom;
      this.exportTo = picked.toDate
        ? moment(picked.toDate).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD");
    },
    async exportMissing() {
      if (!this.exportFrom || !this.exportTo) return;
      const url =
        "api/driverAbsent/exportMissingDeductions?fromDate=" +
        this.exportFrom +
        "&toDate=" +
        this.exportTo;
      try {
        this.exporting = true;
        const response = await this.$http.get(url, { responseType: "blob" });
        this.downloadBlob(response, "MissingDeductions.xlsx");
        this.exportDialog = false;
      } catch (e) {
        this.$notify({
          group: "notification",
          type: "error",
          text: (e && e.body) || "Failed to export missing deductions",
        });
      } finally {
        this.exporting = false;
      }
    },
    pickImport() {
      this.$refs.importFile.click();
    },
    // Step 1: parse the spreadsheet server-side with preview=true (nothing is saved yet) and
    // show the user exactly what would be imported before committing anything.
    async onImportFileSelected(event) {
      const files = event.target.files;
      if (!files || !files.length) return;
      this.importPreviewFile = files[0];
      this.importPreviewDialog = true;
      this.importPreviewLoading = true;
      this.importPreviewRows = [];
      this.importPreviewErrors = [];
      this.importPreviewSkipped = 0;
      try {
        const formData = new FormData();
        formData.append("upload_file", this.importPreviewFile);
        const result = await this.$http.post(
          "api/driverAbsent/importDeductions?preview=true",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        this.importPreviewRows = result.rows || [];
        this.importPreviewErrors = result.errors || [];
        this.importPreviewSkipped = result.skipped || 0;
      } catch (e) {
        this.importPreviewDialog = false;
        this.$notify({
          group: "notification",
          type: "error",
          text: (e && e.body) || "Could not read that spreadsheet",
        });
      } finally {
        this.importPreviewLoading = false;
        event.target.value = "";
      }
    },
    cancelImport() {
      this.importPreviewDialog = false;
      this.importPreviewFile = null;
    },
    // Step 2: the user confirmed — resubmit the same file without preview so it's actually
    // saved.
    async confirmImport() {
      if (!this.importPreviewFile) return;
      try {
        this.importCommitting = true;
        const formData = new FormData();
        formData.append("upload_file", this.importPreviewFile);
        const result = await this.$http.post(
          "api/driverAbsent/importDeductions",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        const errorText = (result.errors || []).slice(0, 3).join(" ");
        this.$notify({
          group: "notification",
          type: result.errors && result.errors.length ? "warning" : "success",
          text:
            (result.imported || 0) +
            " imported, " +
            (result.skipped || 0) +
            " left blank" +
            (errorText ? ". " + errorText : ""),
        });
        this.importPreviewDialog = false;
        this.importPreviewFile = null;
        await this.reloadAll();
      } catch (e) {
        this.$notify({
          group: "notification",
          type: "error",
          text: (e && e.body) || "Import failed",
        });
      } finally {
        this.importCommitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Segmented pill toggle for List / Calendar, matching the nicer style used on Appointments */
.mode-toggle-group {
  display: inline-flex;
  background: #eceff1;
  border-radius: 22px;
  padding: 3px;
}
.mode-toggle-btn {
  display: inline-flex;
  align-items: center;
  border: none;
  background: transparent;
  color: #616161;
  font-size: 13px;
  font-weight: 500;
  padding: 7px 18px;
  border-radius: 18px;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s, box-shadow 0.15s;
}
.mode-toggle-btn:hover:not(.mode-toggle-btn--active) {
  color: #43a047;
}
.mode-toggle-btn--active {
  background-color: #43a047;
  color: #fff !important;
  box-shadow: 0 2px 4px rgba(67, 160, 71, 0.4);
}
.mode-toggle-btn--active .v-icon {
  color: #fff !important;
}
.cal-nav-btn.v-btn {
  min-width: 36px;
  background-color: #fff !important;
}

/* Calendar mode — bordered, colored, "alive" grid instead of a flat gray box */
.cal-sheet {
  border: 2px solid #a5d6a7;
  border-radius: 10px;
  padding: 14px;
  background: #fff;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
}
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
}
.cal-head {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #2e7d32;
  text-align: center;
  padding: 6px 4px;
  background-color: #e8f5e9;
  border-radius: 4px;
}
.cal-day {
  min-height: 96px;
  border: 1px solid #eceff1;
  border-radius: 6px;
  padding: 6px;
  background: #fff;
  transition: box-shadow 0.15s, border-color 0.15s;
}
.cal-day:hover {
  border-color: #c8e6c9;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}
.cal-day--muted {
  background: #fafafa;
}
.cal-day--muted .cal-day-number {
  color: #cfd8dc;
}
.cal-day--today {
  border-color: #66bb6a;
  background: #f6fbf6;
}
.cal-day-number {
  font-size: 12px;
  font-weight: 600;
  color: #757575;
  margin-bottom: 4px;
}
.cal-day-number--today {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 10px;
  background-color: #43a047;
  color: #fff !important;
}
.cal-day-empty {
  height: 4px;
}
.cal-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 500;
  color: #e65100;
  background: linear-gradient(135deg, #fff8e1, #ffe0b2);
  border: 1px solid #ffcc80;
  border-radius: 4px;
  padding: 2px 6px;
  margin-top: 3px;
  cursor: pointer;
  transition: transform 0.1s, box-shadow 0.1s;
}
.cal-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}
.cal-chip-bus {
  font-weight: 600;
}
.import-preview-table {
  font-size: 13px;
}
</style>
